import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  useValidateResetPasswordTokenMutation,
  useResetPasswordMutation,
} from "../../store";
import Alerts from "../../Components/Utils/Alert";

const RESET_SUCCESS_REDIRECT_MS = 2500;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [validateToken, validateResult] = useValidateResetPasswordTokenMutation();
  const [resetPassword, resetResult] = useResetPasswordMutation();

  const [valid, setValid] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [alert, setAlert] = useState(0);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token.trim()) {
      setValid(false);
      return;
    }
    validateToken(token)
      .unwrap()
      .then((data) => {
        setValid(data?.valid === true);
        setRequires2FA(data?.requires2FA === true);
      })
      .catch(() => setValid(false));
  }, [token]);

  useEffect(() => {
    if (resetResult.isSuccess) {
      setAlert(1);
      setMessage(
        resetResult.data?.message ||
          "Şifreniz güncellendi. Yeni şifrenizle giriş yapabilirsiniz."
      );
      const t = setTimeout(() => navigate("/", { replace: true }), RESET_SUCCESS_REDIRECT_MS);
      return () => clearTimeout(t);
    }
    if (resetResult.isError) {
      setAlert(2);
      setMessage(
        resetResult.error?.data?.message ||
          "Şifre güncellenemedi. Link süresi dolmuş veya 2FA kodu hatalı olabilir."
      );
    }
  }, [resetResult.isSuccess, resetResult.isError, resetResult.data?.message, resetResult.error?.data?.message, navigate]);

  useEffect(() => {
    if (alert !== 0) {
      const t = setTimeout(() => {
        setAlert(0);
        setMessage("");
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [alert]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    if (newPassword.length < 8) {
      setAlert(2);
      setMessage("Şifre en az 8 karakter olmalıdır.");
      return;
    }
    if (newPassword !== passwordConfirm) {
      setAlert(2);
      setMessage("Şifreler eşleşmiyor.");
      return;
    }
    if (requires2FA && twoFACode.trim().length !== 6) {
      setAlert(2);
      setMessage("İki adımlı doğrulama kodu 6 haneli olmalıdır.");
      return;
    }
    resetPassword({
      token,
      code: requires2FA ? twoFACode.trim() : undefined,
      newPassword,
      passwordConfirm,
    });
  };

  if (valid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="w-full max-w-md rounded-xl border-2 border-slate-200 bg-white shadow-xl p-8 text-center">
          <p className="text-slate-600">Link doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="w-full max-w-md rounded-xl border-2 border-slate-200 bg-white shadow-xl p-8 text-center space-y-4">
          <h1 className="text-lg font-semibold text-slate-800">Link geçersiz veya süresi doldu</h1>
          <p className="text-slate-600 text-sm">
            Şifre sıfırlama linki 2 dakika geçerlidir. Lütfen tekrar &quot;Şifremi unuttum&quot; ile yeni link alın.
          </p>
          <Link
            to="/"
            className="inline-block w-full py-3 px-4 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700"
          >
            Giriş sayfasına dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      {alert !== 0 && (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex justify-center pt-6 px-4">
          <div className="pointer-events-auto">
            <Alerts AlertChoice={alert} message={message} />
          </div>
        </div>
      )}
      <div className="w-full max-w-md rounded-xl border-2 border-slate-200 bg-white shadow-xl p-6 sm:p-8">
        <h1 className="text-xl font-semibold text-slate-800 mb-2">Yeni şifre belirle</h1>
        <p className="text-slate-600 text-sm mb-6">
          {requires2FA
            ? "Hesabınızda iki adımlı doğrulama açık. Önce 2FA kodunu girin, ardından yeni şifrenizi belirleyin."
            : "Aşağıya yeni şifrenizi girin. Şifre değişince tüm oturumlar sonlandırılır."}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {requires2FA && (
            <div>
              <label className="block text-slate-600 text-sm font-medium mb-1">
                İki adımlı doğrulama kodu (6 hane)
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={twoFACode}
                onChange={(e) =>
                  setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-center text-lg tracking-widest"
              />
            </div>
          )}
          <div>
            <label className="block text-slate-600 text-sm font-medium mb-1">Yeni şifre</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="En az 8 karakter"
              disabled={resetResult.isLoading}
            />
          </div>
          <div>
            <label className="block text-slate-600 text-sm font-medium mb-1">Yeni şifre (tekrar)</label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              minLength={8}
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Şifreyi tekrar girin"
              disabled={resetResult.isLoading}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Link
              to="/"
              className="flex-1 py-3 px-4 rounded-lg border border-slate-300 text-slate-700 text-center font-medium hover:bg-slate-50"
            >
              İptal
            </Link>
            <button
              type="submit"
              disabled={
                resetResult.isLoading ||
                newPassword.length < 8 ||
                newPassword !== passwordConfirm ||
                (requires2FA && twoFACode.trim().length !== 6)
              }
              className="flex-1 py-3 px-4 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetResult.isLoading ? "Güncelleniyor..." : "Şifreyi güncelle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
